// Reads `<meta name="ba-assistant-context" content='...JSON...'>`.
// Used as a fallback when the host can't call init() (static pages).

import type { InitOptions } from '@/domain';

const META_NAME = 'ba-assistant-context';

export function readMetaContext(doc: Document = document): Partial<InitOptions> | null {
  const meta = doc.querySelector(`meta[name="${META_NAME}"]`);
  if (meta === null) return null;
  const raw = meta.getAttribute('content');
  if (raw === null || raw.length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Partial<InitOptions>;
    }
    return null;
  } catch {
    return null;
  }
}

/** Merge meta-tag fallback with the explicit init call (explicit wins). */
export function mergeInit(
  explicit: InitOptions | undefined,
  fallback: Partial<InitOptions> | null,
): InitOptions | null {
  if (explicit === undefined && fallback === null) return null;
  const apiBase = explicit?.apiBase ?? fallback?.apiBase;
  if (apiBase === undefined || apiBase.length === 0) return null;
  return {
    apiBase,
    siteContext: explicit?.siteContext ?? fallback?.siteContext,
    user: explicit?.user ?? fallback?.user,
    // FEATURE-66 — propagate `undefined` when neither side specifies a
    // locale so the chat POST omits the field and the BE auto-detects
    // (FEATURE-42 / FEATURE-56). The previous `?? 'vi'` made BE-side
    // auto-detection unreachable from any host.
    locale: explicit?.locale ?? fallback?.locale,
    theme: explicit?.theme ?? fallback?.theme,
    // FEATURE-88 — let the explicit init win over a meta-tag baseline.
    // Validation + the dev-warning happen in `lifecycle.init`, not here.
    clientCode: explicit?.clientCode ?? fallback?.clientCode,
    position: explicit?.position ?? fallback?.position,
    // Host-customization options (explicit init wins over meta-tag baseline).
    margin: explicit?.margin ?? fallback?.margin,
    width: explicit?.width ?? fallback?.width,
    height: explicit?.height ?? fallback?.height,
    resizable: explicit?.resizable ?? fallback?.resizable,
    icon: explicit?.icon ?? fallback?.icon,
    launcherSize: explicit?.launcherSize ?? fallback?.launcherSize,
    hideLauncher: explicit?.hideLauncher ?? fallback?.hideLauncher,
    accentColor: explicit?.accentColor ?? fallback?.accentColor,
  };
}
